import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Auth from 'core/services/authToken';
import { requirePersonalInformation } from 'shared/helpers';
import { PermissionsService } from 'shared/services';
import { withRouterLegacy } from 'core/routerUtils';
import * as loginActions from 'core/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import * as dashboardActions from 'features/dashboard/duck';

class PrivateRoute extends Component {
  constructor(props) {
    super(props);

    this.state = {
      authenticated: !!Auth.authToken,
      refreshToken: Auth.refreshToken,
      termsLoaded: false,
    };

    this.getProfile = this.getProfile.bind(this);
  }

  componentDidMount() {
    const { currentUser } = this.props;

    if (!this.state.authenticated && this.state.refreshToken) {
      Auth.refreshTokens().then(this.getProfile);
    }

    if (this.state.authenticated && isEmpty(currentUser)) {
      this.getProfile();
      this.props.actions.getRoles();
    }

    if (!isEmpty(this.props.currentUser) && this.props.permissions) {
      PermissionsService.setup(this.props.currentUser, this.props.permissions);
      this.setState({
        permissionsLoaded: true,
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { currentUser } = this.props;

    if (isEmpty(currentUser) && !this.props.userError && !isEmpty(prevProps.currentUser)) {
      Auth.remove();
      this.setState({
        authenticated: false,
        refreshToken: null,
      });
    } else if (
      !isEmpty(prevProps.currentUser) &&
      !isEqual(
        currentUser.testCenterEventPersonnels,
        prevProps.currentUser.testCenterEventPersonnels
      ) &&
      this.props.permissions.length
    ) {
      PermissionsService.setup(currentUser, this.props.permissions);
    }

    if (isEmpty(prevProps.currentUser) && !isEmpty(currentUser) && this.props.userCreated) {
      this.getProfile();
    }

    if (this.state.authenticated && !this.props.roomTiming) {
      this.props.actions.getRoomTiming();
    }
  }

  async getPersonnel() {
    const { actions } = this.props;
    return await Promise.all([
      actions.getPermissions(),
      this.props.currentUser.id && actions.getTestCenterPersonnel(this.props.currentUser.id),
    ]);
  }

  getProfile() {
    const { actions } = this.props;
    actions
      .getUserProfile()
      .then(() => this.getPersonnel())
      .then(() => {
        PermissionsService.setup(this.props.currentUser, this.props.permissions);
        this.setState({
          permissionsLoaded: true,
        });
        if (this.props.currentUser?.id) {
          actions.getAvailableTerms(this.props.currentUser.id, 'active').then(() => {
            this.setState({
              termsLoaded: true,
            });
          });
        }
      });
  }

  render() {
    if (!this.state.authenticated && !this.state.refreshToken) {
      return <Navigate to="/login" replace />;
    }

    if (this.props.userError) {
      if (requirePersonalInformation(this.props.currentUser)) {
        if (this.props.location.pathname !== '/profile/personal-information') {
          return <Navigate to="/profile/personal-information" replace />;
        }
      }
    } else {
      if (
        isEmpty(this.props.currentUser) ||
        !this.state.permissionsLoaded ||
        !this.state.termsLoaded
      ) {
        return null;
      }

      if (!['/dashboard'].includes(this.props.location.pathname) && this.props.userTerms.length) {
        return <Navigate to="/dashboard" replace />;
      }
    }

    const ComponentToRender = this.props.component;
    const routeComponentProps = {
      history: this.props.history,
      location: this.props.location,
      match: this.props.match,
    };

    return ComponentToRender ? <ComponentToRender {...routeComponentProps} /> : null;
  }
}

PrivateRoute.propTypes = {
  // Props from parent/router
  component: PropTypes.oneOfType([PropTypes.elementType, PropTypes.func]).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
    go: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired,
    goForward: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.object.isRequired,
    path: PropTypes.string,
    url: PropTypes.string,
    isExact: PropTypes.bool,
  }).isRequired,

  // Props from mapStateToProps
  currentUser: PropTypes.shape({
    id: PropTypes.number,
    testCenterEventPersonnels: PropTypes.array,
  }),
  userError: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  userCreated: PropTypes.bool,
  token: PropTypes.number,
  errorLogin: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  logOutSuccess: PropTypes.bool.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.object),
  termsLoading: PropTypes.bool,
  userTerms: PropTypes.array,
  roomTiming: PropTypes.array,

  // Props from mapDispatchToProps
  actions: PropTypes.shape({
    getUserProfile: PropTypes.func.isRequired,
    getPermissions: PropTypes.func.isRequired,
    getTestCenterPersonnel: PropTypes.func.isRequired,
    getAvailableTerms: PropTypes.func.isRequired,
    getRoles: PropTypes.func.isRequired,
    getRoomTiming: PropTypes.func.isRequired,
  }).isRequired,
};

function mapStateToProps(state) {
  return {
    currentUser: state.users.currentUser,
    userError: state.users.error,
    userCreated: state.users.userCreated,
    token: state.users.currentUser?.id,
    errorLogin: state.users.error,
    logOutSuccess: state.users.logOutSuccess,
    permissions: state.statics.permissions,
    termsLoading: state.userDashboard.termsLoading,
    userTerms: state.userDashboard.terms,
    roomTiming: state.statics.roomTiming,
  };
}

function mapDispatchToProps(dispatch) {
  // Extract only the action creators (functions), filtering out __esModule, default, etc.
  const allActions = {
    ...loginActions,
    ...dashboardActions,
    ...staticActions,
  };

  const actionCreators = Object.keys(allActions)
    .filter((key) => typeof allActions[key] === 'function')
    .reduce((obj, key) => {
      obj[key] = allActions[key];
      return obj;
    }, {});

  return {
    actions: bindActionCreators(actionCreators, dispatch),
  };
}

export default withRouterLegacy(connect(mapStateToProps, mapDispatchToProps)(PrivateRoute));
